/* eslint-disable tss-unused-classes/unused-classes */
import { makeStyles } from 'tss-react/mui';
import { CustomFilter } from 'app/ComponentLibrary/Filter';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { Collapse, Grid } from '@mui/material';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { useDispatch, useSelector } from 'react-redux';
import { selectGridFiltersExpanded } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { useFormContext } from 'react-hook-form';
import { getPlacementStatusTreeOptions } from 'app/services/SharedServices/SharedServices';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import {
  defaultFilterValues,
  defaultPayPackageStatusOptions,
  PlacementPayPackagesFilterName,
  PlacementPayPackagesFilterSpecs,
  reorderByField,
} from './filterUtils';
import { usePlacementPayPackagesFilterValues } from './usePlacementPayPackagesFilterValues';
import { IGetPlacementPayPackagesQuery } from '@AMIEWEB/Order/Store/types';

const useStyles = makeStyles()({
  fillterCollapse: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '3px',
  },
  collapse: {
    paddingTop: '4px',
  },
});

export const PlacementPayPackagesFilters = ({
  saveFilterPreferences,
  orderId,
  onRefresh,
}: {
  saveFilterPreferences?: (filterValues: any, newSelectedChipFilters?: any) => void;
  orderId: number;
  onRefresh?: () => void;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reset } = useFormContext();
  const filtersExpanded = useSelector(selectGridFiltersExpanded);

  const filterValuesFromApi = usePlacementPayPackagesFilterValues(
    { orderId },
    { includeOrderId: false, includeCandidates: true },
  );
  const gridSpecs = useRef(
    new PlacementPayPackagesFilterSpecs(
      {
        statusOpts: [],
        payPackageStatusOpts: defaultPayPackageStatusOptions,
        candidateIdOpts: [],
        candidateNameOpts: [],
      },
      t,
      { includeCandidateFilters: false, includeOrderIdFilter: false },
    ),
  );
  const baseColumns = gridSpecs.current.getSpecs();

  const orderedColumns = reorderByField(baseColumns, [
    PlacementPayPackagesFilterName.placementStatus,
    PlacementPayPackagesFilterName.payPackageStatus,
    PlacementPayPackagesFilterName.archiveStatus,
  ]);
  const [fields, setFields] = useState<any>(orderedColumns);

  const fetchSpecs = async () => {
    getPlacementStatusTreeOptions().then(res => {
      gridSpecs.current.filterSpecs.statusOpts = res || [];
      const baseColumns = gridSpecs.current.getSpecs();

      const orderedColumns = reorderByField(baseColumns, [
        PlacementPayPackagesFilterName.placementStatus,
        PlacementPayPackagesFilterName.payPackageStatus,
        PlacementPayPackagesFilterName.archiveStatus,
      ]);
      setFields(orderedColumns);
    });
  };

  useEffect(() => {
    gridSpecs.current.filterSpecs.candidateIdOpts = filterValuesFromApi.candidateIdOpts;
    gridSpecs.current.filterSpecs.candidateNameOpts = filterValuesFromApi.candidateNameOpts;
    const baseColumns = gridSpecs.current.getSpecs();

    const orderedColumns = reorderByField(baseColumns, [
      PlacementPayPackagesFilterName.placementStatus,
      PlacementPayPackagesFilterName.payPackageStatus,
      PlacementPayPackagesFilterName.archiveStatus,
    ]);
    setFields(orderedColumns);
  }, [filterValuesFromApi.candidateIdOpts, filterValuesFromApi.candidateNameOpts]);

  const resetFilter = () => {
    dispatch(
      gridStateActions.setFilterAttributes({
        filterObject: Object.entries(defaultFilterValues),
        selectedChipFilters: null,
      }),
    );
    reset({ ...defaultFilterValues });
    if (saveFilterPreferences) {
      saveFilterPreferences(defaultFilterValues, null);
    }
  };

  const refreshData = () => {
    if (onRefresh) {
      onRefresh();
    } else if (Number(orderId) > 0) {
      dispatch(orderDataActions.getPlacementPayPackages({ orderId: Number(orderId) } as IGetPlacementPayPackagesQuery));
    }
  };
  const handleChange = () => {
    dispatch(gridStateActions.setFiltersExpanded(!filtersExpanded));
  };

  useEffect(() => {
    trackPromise(fetchSpecs(), 'placement-pay-packages-filter-specs');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={11}>
        <Collapse timeout={200} in={filtersExpanded} collapsedSize={45} className={classes.collapse}>
          <CustomFilter fields={fields} justifyContent={'flex-start'} version2 resetBtn onResetForm={resetFilter} />
        </Collapse>
      </Grid>
      <Grid item xs={1} className={classes.fillterCollapse}>
        <SearchPageIcons
          resetlbutton
          funnelButton
          onResetForm={() => resetFilter()}
          onAdvancedFilterClick={() => {
            // Funnel icon: reset filters AND re-fetch (mimics "tab switch" clean state)
            handleChange();
          }}
          checked={filtersExpanded}
        />
      </Grid>
    </Grid>
  );
};
